import { useState, FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { signUp } from "@auth/services/cognito";
import { PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL } from "@jkast90/kast-common/legal";
import { LegalLinks } from "@jkast90/kast-common/legal/react";
import { Button, Card, TextField } from "@jkast90/kast-common/ui";
import styles from "./SignUp.module.css";

export default function SignUp() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("redirect") || "";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setIsLoading(true);
    try {
      await signUp({ email, name, password });
      navigate("/check-email" + (redirect ? `?redirect=${encodeURIComponent(redirect)}` : ""), { state: { email } });
    } catch (err) {
      setError((err instanceof Error && err.message) || "Sign up failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className={styles.page}>
      <Card padding="lg" className={styles.card}>
        <img src="/kd_logo.png" alt="Kast Dev" className={styles.logo} />
        <h1 className={styles.title}>Create your Kast Dev account</h1>
        <p className={styles.subtitle}>Join in to play with Kast Dev projects</p>

        {error && (
          <div className={styles.error} role="alert">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="kui-form">
          <TextField
            label="Name"
            type="text"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="Your name"
          />

          <TextField
            label="Email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="you@example.com"
          />

          <TextField
            label="Password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            placeholder="At least 8 characters"
          />

          <TextField
            label="Confirm Password"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            placeholder="Re-enter your password"
          />

          <p className={styles.consent}>
            By creating an account you agree to the{" "}
            <a href={TERMS_OF_SERVICE_URL} target="_blank" rel="noopener noreferrer">
              Terms of Service
            </a>{" "}
            and{" "}
            <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
            .
          </p>

          <Button
            type="submit"
            variant="primary"
            loading={isLoading}
            loadingLabel="Creating account..."
            className={styles.button}
          >
            Sign Up
          </Button>
        </form>

        <p className={styles.loginLink}>
          Already have an account?{" "}
          <a href={`/signin${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ""}`}>Sign in</a>
        </p>

        <LegalLinks className={styles.legalLinks} />
      </Card>
    </div>
  );
}
